import React, { useState, useEffect } from "react";
import { message } from "antd";
import InventoryHeader from "../components/inventoryManagement/InventoryHeader";
import InventoryTable from "../components/inventoryManagement/InventoryTable";
import AddProductsModal from "../components/inventoryManagement/AddProductsModal";
import ManageCategoriesModal from "../components/inventoryManagement/ManageCategoriesModal";
import PromotionModal from "../components/inventoryManagement/PromotionModal";
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  Product,
} from "../hooks/useProducts";
import { useCategories } from "../hooks/useCategories";
import { usePromotion, useUpdatePromotion } from "../hooks/usePromotions";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const InventoryManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useAuth();
  const locationId = user?.location?.id;

  const [isModalVisible, setModalVisible] = useState(false);
  const [isCategoryModalVisible, setCategoryModalVisible] = useState(false);
  const [isPromotionModalVisible, setPromotionModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Get permissions
  const { hasPermission } = usePermissions();
  const canViewInventory = hasPermission(PERMISSIONS.VIEW_INVENTORY);
  const canAddInventory = hasPermission(PERMISSIONS.ADD_INVENTORY);
  const canModifyInventory = hasPermission(PERMISSIONS.MODIFY_INVENTORY);
  const canDeleteInventory = hasPermission(PERMISSIONS.DELETE_INVENTORY);
  const canPostPromotion = hasPermission(PERMISSIONS.POST_PROMOTIONS);
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Initialize filters - only include location_id if user can't see all locations
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      initialFilters.location_id = locationId;
    }
    return initialFilters;
  });

  // Redirect if user doesn't have permission to view inventory
  useEffect(() => {
    if (!canViewInventory) {
      navigate("/unauthorized", {
        state: { from: { pathname: location.pathname } },
      });
    }
  }, [canViewInventory, navigate, location]);

  // Fetch products with currentPage
  const { data: productsData, isLoading } = useProducts(currentPage, filters);

  // Fetch categories
  const { data: categoriesData } = useCategories();

  // Fetch promotion data for ID = 1
  const { data: promotionData } = usePromotion(1);

  // Product mutations
  const { mutate: createProduct } = useCreateProduct();
  const { mutate: updateProduct } = useUpdateProduct();
  const { mutate: deleteProduct } = useDeleteProduct();

  // Promotion mutation
  const { mutate: updatePromotion } = useUpdatePromotion();

  // Product handlers
  const handleAddProduct = () => {
    if (!canAddInventory) {
      message.error("You don't have permission to add products");
      return;
    }
    setSelectedProduct(null);
    setModalVisible(true);
  };

  const handleModifyProduct = (product: Product) => {
    if (!canModifyInventory) {
      message.error("You don't have permission to modify products");
      return;
    }
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const handleDeleteProduct = (productId: number) => {
    if (!canDeleteInventory) {
      message.error("You don't have permission to delete products");
      return;
    }
    deleteProduct(productId);
  };

  // Handle availability toggle
  const handleToggleAvailability = (
    productId: number,
    currentStatus: number
  ) => {
    if (!canModifyInventory) {
      message.error("You don't have permission to modify products");
      return;
    }

    // Find the product to get its current data
    const product = productsData?.data.find((p) => p.id === productId);
    if (!product) {
      message.error("Product not found");
      return;
    }

    // Toggle the status (1 = available, 0 = unavailable)
    const newStatus = currentStatus === 1 ? 0 : 1;

    // Prepare the update data with all required fields
    const updateData = {
      id: productId,
      name: product.name,
      description: product.description,
      qty: product.qty,
      amount: product.amount,
      discount: product.discount,
      status: newStatus,
      category_id: product.category?.id || 0,
      vendor_id: product.vendor_id,
      location_id: [product.location_id], // Assuming location_id is expected as array
      slug: product.slug || product.name.toLowerCase().replace(/\s+/g, "-"), // Generate slug if missing
      imageFile: null, // No image file for status toggle
    };

    updateProduct(updateData, {
      onSuccess: () => {
        const statusText = newStatus === 1 ? "Available" : "Unavailable";
        message.success(`Product marked as ${statusText}`);
      },
      onError: () => {
        message.error("Failed to update product availability");
      },
    });
  };

  // Filter handlers
  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Ensure location filter is maintained for users who can't see all locations
    if (!canSeeAllLocations && locationId) {
      newFilters.location_id = locationId;
    }

    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    const clearedFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      clearedFilters.location_id = locationId;
    }
    setFilters(clearedFilters);
    setCurrentPage(1); // Reset to first page when filters are cleared
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Category handler
  const handleManageCategory = () => {
    if (!canModifyInventory) {
      message.error("You don't have permission to manage categories");
      return;
    }
    setCategoryModalVisible(true);
  };

  // Promotion handler
  const handlePostPromotion = () => {
    if (!canPostPromotion) {
      message.error("You don't have permission to manage promotions");
      return;
    }
    setPromotionModalVisible(true); // Open the promotion modal
  };

  const handlePromotionSubmit = (values: any) => {
    if (!canModifyInventory) {
      message.error("You don't have permission to update promotions");
      return;
    }

    if (promotionData?.data) {
      updatePromotion(
        { ...promotionData.data, ...values }, // Merge fetched data with updated values
        {
          onSuccess: () => {
            message.success("Promotion updated successfully!");
            setPromotionModalVisible(false); // Close the modal
          },
          onError: () => {
            message.error("Failed to update promotion. Please try again.");
          },
        }
      );
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <InventoryHeader
        onManageCategory={handleManageCategory}
        onAddProductClick={handleAddProduct}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        onPostPromotion={handlePostPromotion}
        canAddInventory={canAddInventory}
        canModifyInventory={canModifyInventory}
        canPostPromotion={canPostPromotion}
        canSeeAllLocations={canSeeAllLocations}
      />
      <InventoryTable
        data={productsData?.data || []}
        meta={productsData?.meta || { total: 0, current_page: 1, last_page: 1 }}
        onModifyProduct={handleModifyProduct}
        onDeleteProduct={handleDeleteProduct}
        onToggleAvailability={handleToggleAvailability}
        isLoading={isLoading}
        onPageChange={handlePageChange}
        canModifyInventory={canModifyInventory}
        canDeleteInventory={canDeleteInventory}
      />
      <AddProductsModal
        visible={isModalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={(values) => {
          if (!canAddInventory && !canModifyInventory) {
            message.error(
              "You don't have permission to add or modify products"
            );
            return;
          }

          if (selectedProduct) {
            if (!canModifyInventory) {
              message.error("You don't have permission to modify products");
              return;
            }
            updateProduct({ ...values, id: selectedProduct.id });
          } else {
            if (!canAddInventory) {
              message.error("You don't have permission to add products");
              return;
            }
            createProduct(values);
          }
        }}
        product={selectedProduct}
        canSeeAllLocations={canSeeAllLocations}
        userLocationId={locationId}
      />
      <ManageCategoriesModal
        visible={isCategoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        categories={categoriesData?.data || []}
      />
      <PromotionModal
        visible={isPromotionModalVisible}
        onCancel={() => setPromotionModalVisible(false)}
        onSubmit={handlePromotionSubmit}
        initialValues={promotionData?.data}
      />
    </div>
  );
};

export default InventoryManagementPage;
